import { ACard } from "@atiautomacao/ati-ui-library";
import {
    faBolt,
    faCircle, faShuffle,
    faSolarPanel,
    faSun,
    faTowerCell
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Card, CardContent, CardHeader, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import React, { FunctionComponent } from "react";
import { usePowerStationNavigationToPlant } from "../../../Shared/Hooks/usePowerStationNavigationToPlant";
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Sync } from '@mui/icons-material';

interface Props {
    synopticData: SynoppticCardData;
}

export interface SynoppticCardData {
    powerStation: {
        id: number,
        name: string,
        status: {
            name: string,
            color: string
        },
        isOffline: boolean,
        hasAlarmSound: boolean,
        inverter: {
            status: {
                name: string,
                color: string
            }
            isOffline: boolean,
            allEquipmentOffline: boolean,
            stoppedCount: number,
            pendingCount: number,
            highCount: number,
            withoutCommunicationCount: number

        },
        relay: {
            status: {
                name: string,
                color: string
            },
            isOffline: boolean,
            allEquipmentOffline: boolean
            stoppedCount: number,
            pendingCount: number,
            highCount: number,
            withoutCommunicationCount: number

        },
        solarimetric: {
            status: {
                name: string,
                color: string
            },
            isOffline: boolean,
            allEquipmentOffline: boolean,
            stoppedCount: number,
            pendingCount: number,
            highCount: number,
            withoutCommunicationCount: number

        },
        trackers: {
            status: {
                name: string,
                color: string
            },
            stoppedCount: number,
            pendingCount: number,
            highCount: number
            isOffline: boolean,
            allEquipmentOffline: boolean,
            withoutCommunicationCount: number
        }
    }
}

const isDarkColor = (hex: string)=> {
    hex = hex.replace('#', '');

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    return luminance < 128;
}


const cutString = (input: string, limit: number = 20): string => {
    const toDivide = (str: string): string[] => {
        return str.split(/(?=[ -])|(?<=[ -])/);
    };

    const isAbbreviation = (str: string): boolean =>
        str.endsWith('.') || /^[A-Z]{1,3}(\.|$)/.test(str);

    const abbreviateGreater = (parts: string[]): string[] => {
        let maxIndex = -1;
        let maxLength = 0;

        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (![" ", "-"].includes(part) && !isAbbreviation(part) && part.length > maxLength) {
                maxIndex = i;
                maxLength = part.length;
            }
        }

        if (maxIndex >= 0 && parts[maxIndex].length > 3) {
            parts[maxIndex] = parts[maxIndex].slice(0, 3) + '.';
        }

        return parts;
    };

    let parts = toDivide(input);

    while (parts.join('').length > limit) {
        const old = [...parts];
        parts = abbreviateGreater(parts);
        if (old.join('') === parts.join('')) break;
    }

    return parts.join('');
}


function areEqual(prevProps: Props, nextProps: Props) {
    return JSON.stringify(prevProps.synopticData) === JSON.stringify(nextProps.synopticData);
}

export const getInverterBox = (inverter: Props["synopticData"]["powerStation"]["inverter"]) => {
    return <Box sx={{
                position: 'relative',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                top:
                    (inverter.allEquipmentOffline && inverter.stoppedCount > 0)
                        ? '0px'                                   // <-- não empurra nesses casos
                        : ((inverter.isOffline || inverter.stoppedCount > 0) ? '3px' : '0px'),
                right: '5px'
            }}>
                {inverter.allEquipmentOffline ?
                    <Badge badgeContent={inverter.stoppedCount}
                            invisible={inverter.stoppedCount === 0}
                            color="error"
                            sx={{
                        '& .MuiBadge-badge': {
                            fontSize: '0.6rem',
                            height: '14px',
                            minWidth: '14px',
                            padding: '0 4px',
                            left: 0,
                            right: 'auto',
                            '&:not(.MuiBadge-invisible)': {
                                transform: 'translate(-50%, -50%)',
                            },
                        },
                    }}>
                        <Tooltip title={
                            <Typography sx={{display: 'block', fontSize: '0.7rem'}} align="center">
                                Inversores<br/>Status: Sem comunicação
                            </Typography>
                        } arrow>
                            <Box sx={{
                                position: 'relative',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginTop: 0.5,

                            }}>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: 30,
                                        height: 30,
                                        backgroundColor: '#ff8d28',
                                        borderRadius: '50%',
                                        zIndex: 0,
                                    }}
                                />
                                <FontAwesomeIcon
                                    icon={faTowerCell}
                                    size="2x"
                                    color="#ffffff"
                                    style={{zIndex: 1, fontSize: '1.3rem'}}
                                />
                            </Box>
                        </Tooltip>
                    </Badge>
                    : (
                        <Badge badgeContent={inverter.stoppedCount} color="error"
                                invisible={inverter.stoppedCount === 0}
                                sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '0.6rem',
                                height: '14px',
                                minWidth: '14px',
                                padding: '0 4px',
                                left: 0,
                                right: 'auto',
                                '&:not(.MuiBadge-invisible)': {
                                    transform: 'translate(-50%, -50%)',
                                },
                            },
                        }}>
                            <Tooltip title={inverter.status.name === "Default" ? "Inversores não cadastrados" : (
                                <Typography  sx={{ display: 'block', fontSize: '0.7rem' }} align="center">
                                    Inversores<br />Status: {inverter.status.name}
                                </Typography>
                            )} arrow>
                                <FontAwesomeIcon
                                    color={`#${inverter.status.color}`} icon={faShuffle} size={'2x'}/>
                            </Tooltip>
                            {inverter.isOffline ?
                                <Box sx={{ position: 'relative'}}>
                                    <FontAwesomeIcon icon={faCircle} size="2x" color="#e6b400" style={{ position: 'absolute', zIndex: 0, top: '-6px', right: '-5px',  fontSize: '0.8rem',}} />
                                    <FontAwesomeIcon icon={faTowerCell} size="1x" color="#ffffff" style={{ position: 'absolute', zIndex: 1, top: '-4px', right: '-3px',  fontSize: '0.5rem' }} />
                                </Box>
                                : <></>}
                        </Badge>
                    )
                }
            </Box>
}

export const getRelayBox = (relay: Props["synopticData"]["powerStation"]["relay"]) => {
    return <Box sx={{
                        position: 'relative',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        top:
                            (relay.allEquipmentOffline && relay.stoppedCount > 0)
                                ? '0px'                                   // <-- não empurra nesses casos
                                : ((relay.isOffline || relay.stoppedCount > 0) ? '3px' : '0px'),
                        right: relay.isOffline && relay.stoppedCount ? '2px' : '4px'
                    }}>
                        {relay.allEquipmentOffline ?
                            <Badge invisible={relay.stoppedCount === 0}  badgeContent={relay.stoppedCount} color="error" sx={{
                                '& .MuiBadge-badge': {
                                    fontSize: '0.6rem',
                                    height: '14px',
                                    minWidth: '14px',
                                    padding: '0 4px',
                                    left: 0,
                                    right: 'auto',
                                    '&:not(.MuiBadge-invisible)': {
                                        transform: 'translate(-50%, -50%)',
                                    },

                                },
                            }}>
                                <Tooltip title={
                                    <Typography sx={{display: 'block', fontSize: '0.7rem'}} align="center">
                                        Relés<br/>Status: Sem comunicação
                                    </Typography>
                                } arrow>
                                    <Box sx={{
                                        position: 'relative',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginTop: 0.5
                                    }}>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                width: 30,
                                                height: 30,
                                                backgroundColor: '#ff8d28',
                                                borderRadius: '50%',
                                                zIndex: 0,
                                            }}
                                        />
                                        <FontAwesomeIcon
                                            icon={faTowerCell}
                                            size="2x"
                                            color="#ffffff"
                                            style={{zIndex: 1, fontSize: '1.3rem'}}
                                        />
                                    </Box>
                                </Tooltip>
                            </Badge>
                            : (
                                <Badge invisible={relay.stoppedCount === 0} badgeContent={relay.stoppedCount} color="error" sx={{
                                    '& .MuiBadge-badge': {
                                        fontSize: '0.6rem',
                                        height: '14px',
                                        minWidth: '14px',
                                        padding: '0 4px',
                                        left: 0,
                                        right: 'auto',
                                        '&:not(.MuiBadge-invisible)': {
                                            transform: 'translate(-50%, -50%)',
                                        },
                                    },
                                }}>
                                    <Tooltip title={relay.status.name === "Default" ? "Relés não cadastrados" : (
                                        <Typography  sx={{ display: 'block', fontSize: '0.7rem' }} align="center">
                                            Relés<br />Status: {relay.status.name}
                                        </Typography>
                                    )} arrow>
                                        <FontAwesomeIcon
                                            color={`#${relay.status.color}`} icon={faBolt} size={'2x'}/>
                                    </Tooltip>
                                    {relay.isOffline ?
                                        <Box sx={{ position: 'relative'}}>
                                            <FontAwesomeIcon icon={faCircle} size="2x" color="#e6b400" style={{ position: 'absolute', zIndex: 0, top: '-6px', right: '-5px',  fontSize: '0.8rem',}} />
                                            <FontAwesomeIcon icon={faTowerCell} size="1x" color="#ffffff" style={{ position: 'absolute', zIndex: 1, top: '-4px', right: '-3px',  fontSize: '0.5rem' }} />
                                        </Box>
                                        : <></>}
                                </Badge>
                            )
                        }
                    </Box>
}

export const getSolarimetricBox = (solarimetric: Props["synopticData"]["powerStation"]["solarimetric"]) => {
    return <Box sx={{
                                position: 'relative',
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                right: solarimetric.isOffline ? '1px' : '2px',
                                bottom: solarimetric.isOffline ? '1px' : '0px'
                            }}>
                        {solarimetric.allEquipmentOffline ?
                            <Tooltip title={
                                <Typography sx={{ display: 'block', fontSize: '0.7rem' }} align="center">
                                    Estação solarimétrica<br />Status: Sem comunicação
                                </Typography>
                            } arrow>
                                <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 0.5 }}>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            width: 30,
                                            height: 30,
                                            backgroundColor: '#ff8d28',
                                            borderRadius: '50%',
                                            zIndex: 0,
                                        }}
                                    />
                                    <FontAwesomeIcon
                                        icon={faTowerCell}
                                        size="2x"
                                        color="#ffffff"
                                        style={{ zIndex: 1 , fontSize: '1.3rem'}}
                                    />
                                </Box>
                            </Tooltip>
                            : (
                                <Tooltip title={solarimetric.status.name === "Default" ?  "Estação solarimétrica não cadastrada" :
                                    <Typography  sx={{ display: 'block', fontSize: '0.7rem' }} align="center">
                                        Estação solarimétrica<br />Status: {solarimetric.status.name}
                                    </Typography>} arrow>
                                    <FontAwesomeIcon color={`#${solarimetric.status.color}`} icon={faSun} size="2x" />
                                </Tooltip>
                            )

                        }
                    </Box>
}

export const getTrackersBox = (trackers: Props["synopticData"]["powerStation"]["trackers"]) => {
    return <Box sx={{
                                position: 'relative',
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                top: trackers.isOffline ?  '2px': '0px',
                                left: '4px'
                            }}>
                        {trackers.allEquipmentOffline ?
                            <Badge invisible={trackers.stoppedCount === 0} badgeContent={trackers.stoppedCount} color="error" sx={{
                                '& .MuiBadge-badge': {
                                    fontSize: '0.6rem',
                                    height: '14px',
                                    minWidth: '14px',
                                    padding: '0 4px',
                                    left: 0,
                                    right: 'auto',
                                    '&:not(.MuiBadge-invisible)': {
                                        transform: 'translate(-50%, -50%)',
                                    },
                                },
                            }}>
                                <Tooltip title={
                                    <Typography sx={{display: 'block', fontSize: '0.7rem'}} align="center">
                                        Trackers<br/>Status: Sem comunicação
                                    </Typography>
                                } arrow>
                                    <Box sx={{
                                        position: 'relative',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginTop: 0.5
                                    }}>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                width: 30,
                                                height: 30,
                                                backgroundColor: '#ff8d28',
                                                borderRadius: '50%',
                                                zIndex: 0,
                                            }}
                                        />
                                        <FontAwesomeIcon
                                            icon={faTowerCell}
                                            size="2x"
                                            color="#ffffff"
                                            style={{zIndex: 1, fontSize: '1.3rem'}}
                                        />
                                    </Box>
                                </Tooltip>
                            </Badge>
                            : (
                                <Badge invisible={trackers.stoppedCount === 0 } badgeContent={trackers.stoppedCount} color="error" sx={{
                                    '& .MuiBadge-badge': {
                                        fontSize: '0.6rem',
                                        height: '14px',
                                        minWidth: '14px',
                                        padding: '0 4px',
                                        left: 0,
                                        right: 'auto',
                                        '&:not(.MuiBadge-invisible)': {
                                            transform: 'translate(-50%, -50%)',
                                        },
                                    },
                                }}>
                                    <Tooltip title={trackers.status.name === "Default" ? "Trackers não cadastrados" : (
                                        <Typography  sx={{ display: 'block', fontSize: '0.7rem' }} align="center">
                                            Trackers<br />Status: {trackers.status.name}
                                        </Typography>
                                    )} arrow>
                                        <FontAwesomeIcon
                                            color={`#${trackers.status.color}`} icon={faSolarPanel} size={'2x'}/>
                                    </Tooltip>
                                    {trackers.isOffline ?
                                        <Box sx={{ position: 'relative'}}>
                                            <FontAwesomeIcon icon={faCircle} size="2x" color="#e6b400" style={{ position: 'absolute', zIndex: 0, top: '-6px', right: '-5px',  fontSize: '0.8rem',}} />
                                            <FontAwesomeIcon icon={faTowerCell} size="1x" color="#ffffff" style={{ position: 'absolute', zIndex: 1, top: '-4px', right: '-3px',  fontSize: '0.5rem' }} />
                                        </Box>
                                        : <></>}
                                </Badge>
                            )
                        }
                    </Box>
}   

const SynopticCard: FunctionComponent<Props> = ({synopticData}:Props) => {
    const powerStation = synopticData.powerStation
    const inverter = synopticData.powerStation.inverter
    const relay = synopticData.powerStation.relay
    const solarimetric = synopticData.powerStation.solarimetric
    const trackers = synopticData.powerStation.trackers
    const navigationToPlantByPowerStationId = usePowerStationNavigationToPlant();
    const theme = useTheme();
    const styleProps = {
                    contentStyle: {
                        height: 45,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                    },
                    titleStyle: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        margin: 0,
                        fontSize: 14,
                        padding: 0, color: isDarkColor(powerStation.status.color) ? '#ffffff' : '#000000'},
                    cardStyle: {
                        padding: 0,
                        width: 190
                    },
                    headerStyle: {
                        backgroundColor: `#${powerStation.status.color}`, height: 45,
                        cursor: 'pointer',
                    }
                };

    const PulseCard = {
            animation: "pulse 2s infinite",
            "@keyframes pulse": {
                    "0%": {
                    transform: "scale(1)",
                    },
                    "70%": {
                    transform: "scale(1.07)",
                    },
                    "100%": {
                    transform: "scale(1)",
                    },
                },
            };        

    const pulsating =  powerStation.trackers.pendingCount > 0 ||
                        powerStation.inverter.pendingCount > 0 ||
                        powerStation.relay.pendingCount > 0 ||
                        powerStation.solarimetric.pendingCount > 0;

    return (
            <Card
                onClick={() => navigationToPlantByPowerStationId(powerStation.id)}
                sx={{
                    height: '100%',
                    borderRadius: 4,
                    ...styleProps.cardStyle,
                    ...(pulsating ? PulseCard : {}),
                }}

            >
                <Tooltip title={ !powerStation ? '' :
                        <Typography  sx={{ display: 'block', fontSize: '0.7rem', whiteSpace: 'pre-line' }} align="center">
                            {powerStation.name ? `${powerStation.name}\nStatus: ${powerStation.status.name}` : ""}
                        </Typography>
                    }   
                arrow
                PopperProps={{
                                modifiers: [{ name: 'offset', options: { offset: [0, -20] } }],
                        }}>
                        <CardHeader
                            style={{
                            ...styleProps?.headerStyle,
                            }}
                            title={<div style={{ ...styleProps.titleStyle  }}>
                                        {powerStation && powerStation.hasAlarmSound &&  <VolumeUpIcon fontSize="small" sx={{ verticalAlign: 'middle', marginRight: 0.5, color: theme.palette.primary.main }}/> }
                                        {powerStation.name ? cutString(powerStation.name) : ""}
                                        {powerStation.status.name === "Sincronizando" && (
                                            <Sync fontSize="small" sx={{ verticalAlign: 'middle', marginLeft: 0.5, color: isDarkColor(powerStation.status.color) ? '#ffffff' : '#000000' }}/>
                                        )}
                                    </div>}
                            disableTypography={true}
                        />
                </Tooltip>
                <CardContent style={{ fontSize: 12, ...styleProps.contentStyle }}>
                    <Grid

                        sx={{
                            display: 'flex',
                            justifyContent: 'center', cursor: 'pointer',
                            alignItems: 'center',
                            gap: 0
                            }}>

                        {getInverterBox(inverter)}
                        {getRelayBox(relay)}
                        {getSolarimetricBox(solarimetric)}
                        {getTrackersBox(trackers)}

                    </Grid>
                </CardContent>  
            </Card>
    )
}

export default React.memo(SynopticCard, areEqual);